import { Component, Input, OnInit } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import {
  FRAME_MANUFACTURERS,
  SystemManufacturer,
  ProfileSystem,
  FrameProfile,
  ProfileWindowsMapping
} from '../../utils/frame-brands.data';
import { getTranslatedPositionLabel, getExpertImagePath } from 'src/app/utils/Constants';
import { StorageService } from 'src/app/services/storage.service';
import { ModalHelperService } from 'src/app/services/modal-helper';
import { MasterdataApiService } from 'src/app/services/masterdata-api.service';
import { CalculationAutoUpdateService } from 'src/app/services/calculation-auto-update.service';
import { SystemSelectPopoverComponent } from '../system-select-popover/system-select-popover.component';
import { calculateAreaWeightedUf } from 'src/app/utils/calculation';
import { TranslateService } from '@ngx-translate/core';

interface MappedProfile {
  position: string;
  label: string;
  profile: FrameProfile | null;
  allProfiles: FrameProfile[];
  relIds: number[];
  selectedRelId: number;
  expertImage: string;
}

@Component({
  selector: 'app-frame-profiles-modal',
  templateUrl: './frame-profiles-modal.component.html',
  styleUrls: ['./frame-profiles-modal.component.scss'],
  standalone: false,
})
export class FrameProfilesModalComponent implements OnInit {
  @Input() selectedManufacturerId!: number;
  @Input() selectedSystemId!: number;
  @Input() windowTypeId!: number;
  @Input() isComparisonMode: boolean = false;

  manufacturers: SystemManufacturer[] = [];
  selectedManufacturer: SystemManufacturer | null = null;
  filteredSystems: ProfileSystem[] = [];
  selectedSystem: ProfileSystem | null = null;
  mappedProfiles: MappedProfile[] = [];
  glazingValue: number = 3;

  constructor(private modalController: ModalController, private storageService: StorageService,
    private modalHelper: ModalHelperService, private masterdataApi: MasterdataApiService, private calcAutoUpdate: CalculationAutoUpdateService,
    private popoverController: PopoverController, private translate: TranslateService) { }

  async ngOnInit() {
    await this.loadManufacturersFromMasterdata();
    await this.loadGlazingValue();
    this.loadInitialData();
  }

  private async loadManufacturersFromMasterdata(): Promise<void> {
    // Try cached masterdata written by MasterdataApiService.loadMasterData()
    const cached = await this.masterdataApi.getCachedMasterData();
    if (cached?.frameManufacturers && cached.frameManufacturers.length) {
      this.manufacturers = cached.frameManufacturers as SystemManufacturer[];
      console.log('Loaded frame manufacturers from masterdata:', this.manufacturers);
      return;
    }
    this.manufacturers = FRAME_MANUFACTURERS;

    // this.manufacturers = []; // or fallback
  }


  loadInitialData() {
    // Load manufacturer
    this.selectedManufacturer = this.manufacturers.find(m => m.id === this.selectedManufacturerId) || null;

    if (this.selectedManufacturer) {
      this.filteredSystems = this.selectedManufacturer.systems;

      // Load system
      this.selectedSystem = this.filteredSystems.find(s => s.id === this.selectedSystemId) || null;

      if (this.selectedSystem) {
        this.loadMappedProfiles();
      }
    }
  }

  async loadGlazingValue() {
    try {
      const projectData = this.isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        this.glazingValue = project.windowglas?.glazingCount || 3;
        console.log('Loaded glazing value:', this.glazingValue);
      }
    } catch (error) {
      console.error('Error loading glazing value:', error);
      this.glazingValue = 3; // Default to triple glazing
    }
  }

  onManufacturerChange() {
    this.selectedManufacturer = this.manufacturers.find(m => m.id === this.selectedManufacturerId) || null;
    this.filteredSystems = this.selectedManufacturer?.systems || [];

    // Reset system
    this.selectedSystemId = this.filteredSystems.length > 0 ? this.filteredSystems[0].id : 0;
    this.onSystemChange();
  }

  onSystemChange() {
    this.selectedSystem = this.filteredSystems.find(s => s.id === this.selectedSystemId) || null;

    if (this.selectedSystem) {
      this.loadMappedProfiles();
    } else {
      this.mappedProfiles = [];
    }
  }

  loadMappedProfiles() {
    if (!this.selectedManufacturer || !this.selectedSystem) return;

    const mapping = this.getWindowMapping(this.selectedManufacturerId, this.windowTypeId);

    if (!mapping) {
      console.error('No mapping found for window type:', this.windowTypeId);
      return;
    }

    const frameCount = this.getFrameCountForWindowType(this.windowTypeId);
    // const positionLabels = [
    //   { key: 'f0', label: getPositionLabel(this.windowTypeId, 'f0') },
    //   { key: 'f1', label: getPositionLabel(this.windowTypeId, 'f1') },
    //   { key: 'f2', label: getPositionLabel(this.windowTypeId, 'f2') },
    //   { key: 'f3', label: getPositionLabel(this.windowTypeId, 'f3') },
    //   { key: 'f4', label: getPositionLabel(this.windowTypeId, 'f4') },
    //   { key: 'f5', label: getPositionLabel(this.windowTypeId, 'f5') },
    //   { key: 'f6', label: getPositionLabel(this.windowTypeId, 'f6') }
    // ];
    const positionLabels = [];
    for (let i = 0; i < frameCount; i++) {
      const key = `f${i}`;
      positionLabels.push({
        key: key,
        label: getTranslatedPositionLabel(this.windowTypeId, key, this.translate)
      });
    }

    this.mappedProfiles = positionLabels.map(pos => {
      const relIds = (mapping as any)[pos.key] as number[];

      if (!relIds || relIds.length === 0) {
        return null;
      }
      //Get all profiles for this position
      const allProfiles = relIds
        .map(relId => this.getFrameProfileByRelId(this.selectedSystem!, relId))
        .filter(p => p !== undefined) as FrameProfile[];

      const selectedRelId = relIds[0];
      const profile = this.getFrameProfileByRelId(this.selectedSystem!, selectedRelId);

      return {
        position: pos.key,
        label: pos.label,
        profile: profile || null,
        allProfiles: allProfiles,
        relIds: relIds,
        selectedRelId: selectedRelId,
        expertImage: getExpertImagePath(this.windowTypeId, pos.key)
      };
    }).filter(item => item !== null) as MappedProfile[];

    console.log('Mapped profiles for window type', this.windowTypeId, ':', this.mappedProfiles);
  }

  //Handle profile selection from dropdown
  onProfileChange(mapped: MappedProfile, relId: number) {
    const newProfile = this.getFrameProfileByRelId(this.selectedSystem!, relId);
    if (newProfile) {
      mapped.profile = newProfile;
      mapped.selectedRelId = relId;
      console.log(`Profile changed for ${mapped.position}:`, newProfile.name);
    }
  }

  //Check if position has multiple profiles
  hasMultipleProfiles(mapped: MappedProfile): boolean {
    return mapped.allProfiles.length > 1;
  }

  getFrameCountForWindowType(windowTypeId: number): number {
    const frameCounts: { [key: number]: number } = {
      1: 3,   // Turn-tilt window (3 frames)
      2: 4,
      3: 7,
      4: 6,
      5: 5,
      6: 7,
      7: 5,
      8: 6,
      9: 3,
      10: 4,
      11: 5,
      12: 7,
      13: 4,
      14: 4,
      15: 4,
      16: 6
    };
    return frameCounts[windowTypeId] || 4;
  }
  getWindowMapping(manufacturerId: number, windowTypeId: number): ProfileWindowsMapping | undefined {
    const manufacturer = this.manufacturers.find(m => m.id === manufacturerId);
    return manufacturer?.windowsMap.find(m => m.wid === windowTypeId);
  }

  getFrameProfileByRelId(system: ProfileSystem, relId: number): FrameProfile | undefined {
    return system.profiles.find(p => p.relIDs.includes(relId));
  }

  getDisplayableUf(uf: (number | null)[]): string {
    const index = this.glazingValue - 1;
    if (index >= 0 && index < uf.length && uf[index] !== null) {
      return uf[index]!.toString();
    }
    return 'N/A';
  }

  back() {
    this.modalController.dismiss(null, 'back');
  }

  async validate() {
    if (!this.selectedManufacturer || !this.selectedSystem || this.mappedProfiles.length === 0) {
      return;
    }
    const frame = this.buildByBrandFrameData();
    if (!frame) return;

    try {
      const projectData = this.isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();

      const project = projectData ? JSON.parse(projectData) : null;

      const dimensions = {
        a: parseFloat(project?.dimensions?.a || 0),
        b: parseFloat(project?.dimensions?.b || 0),
        c: parseFloat(project?.dimensions?.c || 0)
      };

      const frameWidths = {
        w0: frame.wValues[0] || 0,
        w1: frame.wValues[1] || 0,
        w2: frame.wValues[2] || 0,
        w3: frame.wValues[3] || 0,
        w4: frame.wValues[4] || 0,
        w5: frame.wValues[5] || 0,
        w6: frame.wValues[6] || 0
      };

      const weightedUf = calculateAreaWeightedUf(
        this.windowTypeId,
        dimensions,
        frameWidths,
        frame.ufValues
      );

      frame.uf = weightedUf;

      if (this.isComparisonMode) {
        await this.storageService.updateComparedProject({ windowframe: frame });
      } else {
        await this.storageService.updateProject({ windowframe: frame });
        await this.calcAutoUpdate.triggerAutoUpdate('frame', this.isComparisonMode);
      }

      console.log('Frame saved to storage:', frame);
    } catch (error) {
      console.error('Error saving frame:', error);
    }

    await this.modalController.dismiss({ frame }, 'confirm');
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }

  private getDisplayableUfValue(uf: (number | null)[]): number {
    const index = this.glazingValue - 1;
    if (index >= 0 && index < uf.length && uf[index] !== null) {
      return uf[index]!;
    }
    return 0;
  }

  private buildByBrandFrameData(): any {
    if (!this.selectedManufacturer || !this.selectedSystem || this.mappedProfiles.length === 0) {
      return null;
    }

    const ufValues: number[] = new Array(7).fill(0);
    const wValues: number[] = new Array(7).fill(0);

    const frameCount = this.getFrameCountForWindowType(this.windowTypeId);
    // let ufSum = 0, ufCount = 0;

    for (let i = 0; i < frameCount; i++) {
      const mapped = this.mappedProfiles.find(mp => mp.position === `f${i}`);
      if (mapped?.profile) {
        const ufVal = this.getDisplayableUfValue(mapped.profile.uf);
        const wVal = mapped.profile.wf || 0;
        ufValues[i] = ufVal;
        wValues[i] = wVal;
        // if (ufVal > 0) { ufSum += ufVal; ufCount++; }
      }
    }

    return {
      id: -1,
      type: this.selectedSystem.frameType || 'FRAME_TYPE_PVC',
      uf: 0,
      visibleWidth: wValues.find(w => w > 0) || 0.11,
      name: `${this.selectedManufacturer.name} ${this.selectedSystem.name}`,
      ufValues,
      wValues,
      system: {
        manufacturerId: this.selectedManufacturerId,
        systemId: this.selectedSystemId,
      }
    };
  }

  async openSystemPopover(ev: Event) {
    const popover = await this.popoverController.create({
      component: SystemSelectPopoverComponent,
      event: ev,
      translucent: false,
      side: 'bottom',
      alignment: 'start',
      cssClass: 'system-dropdown-popover',
      componentProps: {
        systems: this.filteredSystems,
        selectedSystemId: this.selectedSystemId
      }
    });

    await popover.present();

    const { data, role } = await popover.onWillDismiss();

    if (role === 'confirm' && data?.system) {
      this.selectedSystemId = data.system.id;
      this.selectedSystem = data.system;
      this.onSystemChange();
    }
  }
}
